import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type { Task, TaskCreate, TaskStatus } from "@/api/types";
import { ACTIVE_TASK_STATUSES } from "@/api/types";

const KEY = ["tasks"] as const;
const KEY_ITEM = (taskId: string) => ["task", taskId] as const;
const KEY_SCHEDULE_EXECUTIONS = (scheduleId: string) =>
  ["task-schedule-executions", scheduleId] as const;

export function isTaskActive(status: TaskStatus): boolean {
  return ACTIVE_TASK_STATUSES.includes(status);
}

export interface TasksFilters {
  status?: TaskStatus;
  agent_id?: string;
  schedule_id?: string;
}

export function useTasks(filters: TasksFilters = {}) {
  return useQuery({
    queryKey: [...KEY, filters],
    queryFn: () => {
      const params: Record<string, string> = {};
      if (filters.status) params.status = filters.status;
      if (filters.agent_id) params.agent_id = filters.agent_id;
      if (filters.schedule_id) params.schedule_id = filters.schedule_id;
      return api.GET<Task[]>("/tasks", { params });
    },
    // Polling condicional: 5s quando ha task ativa (status muda no
    // worker, sem SSE); 30s baseline para capturar tasks agendadas que
    // comecaram em background (mesmo padrao do inbox HITL).
    refetchInterval: (query) =>
      query.state.data?.some((t) => isTaskActive(t.status)) ? 5_000 : 30_000,
  });
}

export function useTask(taskId: string) {
  return useQuery({
    queryKey: KEY_ITEM(taskId),
    queryFn: () => api.GET<Task>(`/tasks/${taskId}`),
    enabled: Boolean(taskId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && isTaskActive(status) ? 3_000 : false;
    },
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: TaskCreate) => api.POST<Task>("/tasks", payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useCancelTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (task: Task) =>
      api.POST<Task>(`/tasks/${task.id}/cancel`, {}),
    onSuccess: (_data, task) => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: KEY_ITEM(task.id) });
      if (task.schedule_id) {
        void qc.invalidateQueries({
          queryKey: KEY_SCHEDULE_EXECUTIONS(task.schedule_id),
        });
      }
    },
  });
}

export { KEY as TASKS_KEY, KEY_ITEM as TASK_ITEM_KEY };
